import { useEffect, useState } from "react";
import api from "@/lib/api";
import { downloadPdf } from "@/lib/files";
import { fmtDA } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { FileDown, CalendarDays, Loader2 } from "lucide-react";

const nf = (n) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 }).format(n || 0);

const Two = ({ q, ht, bold }) => (
  <td className={`py-1.5 px-2 tabular text-right whitespace-nowrap ${bold ? "font-bold" : ""}`}>
    <div>{nf(q)}</div>
    <div className={`text-[11px] ${bold ? "text-success" : "text-muted-foreground"}`}>{fmtDA(ht)}</div>
  </td>
);

const SocieteCaMensuel = ({ s }) => {
  const sum = (f) => s.rows.reduce((a, r) => a + f(r), 0);
  return (
  <div className="min-w-0" data-testid={`ca-mensuel-societe-${s.societe_id}`}>
    <p className="text-sm font-semibold mb-2">{s.societe}</p>
    <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead><tr className="text-left text-muted-foreground border-b align-bottom">
        <th className="py-2 pr-2 font-medium">Mois</th>
        {s.produits.map((p) => <th key={p} className="py-2 px-2 font-medium text-right text-xs max-w-[120px]">{p}</th>)}
        <th className="py-2 px-2 font-medium text-right whitespace-nowrap">Qté totale</th>
        <th className="py-2 font-medium text-right whitespace-nowrap">CA HT total</th>
      </tr></thead>
      <tbody className="divide-y">
        {s.rows.map((r) => (
          <tr key={r.mois} className={r.ht ? "" : "text-muted-foreground"}>
            <td className="py-2 pr-2">{r.mois}</td>
            {s.produits.map((p) => <Two key={p} q={r.produits[p].q} ht={r.produits[p].ht} />)}
            <td className="py-2 px-2 tabular text-right font-medium">{nf(r.q)}</td>
            <td className="py-2 tabular text-right whitespace-nowrap">{fmtDA(r.ht)}</td>
          </tr>
        ))}
        <tr className="font-bold border-t-2 border-primary/30" data-testid={`ca-mensuel-total-${s.societe_id}`}>
          <td className="py-2 pr-2">TOTAL</td>
          {s.produits.map((p) => <Two key={p} q={sum((r) => r.produits[p].q)} ht={sum((r) => r.produits[p].ht)} bold />)}
          <td className="py-2 px-2 tabular text-right">{nf(sum((r) => r.q))}</td>
          <td className="py-2 tabular text-right text-success whitespace-nowrap">{fmtDA(sum((r) => r.ht))}</td>
        </tr>
      </tbody>
    </table>
    </div>
  </div>
  );
};

export const CaMensuelCard = ({ societeId, exercice }) => {
  const [data, setData] = useState(null);
  const annee = exercice === "all" ? new Date().getFullYear() : exercice;
  const params = { annee, ...(societeId !== "all" && { societe_id: societeId }) };

  useEffect(() => {
    setData(null);
    api.get("/pdf/ca-mensuel/data", { params }).then((r) => setData(r.data)).catch(() => setData([]));
  }, [societeId, exercice]); // eslint-disable-line

  return (
    <div className="rounded-xl border bg-card p-5 mt-6" data-testid="dashboard-ca-mensuel">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-primary" />
          <h3 className="font-heading font-bold">Chiffre d'affaires par mois et par produit (quantité &amp; valeur HT) <span className="text-sm font-normal text-muted-foreground">({annee})</span></h3>
        </div>
        <Button variant="outline" size="sm" className="gap-2" data-testid="ca-mensuel-pdf-btn"
          onClick={() => downloadPdf(`/pdf/ca-mensuel?${new URLSearchParams(params).toString()}`, `ca-mensuel-${annee}`)}>
          <FileDown className="h-4 w-4" /> PDF
        </Button>
      </div>
      {data === null
        ? <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        : <div className="grid grid-cols-1 gap-8">{data.map((s) => <SocieteCaMensuel key={s.societe_id} s={s} />)}</div>}
    </div>
  );
};
